import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import ScheduleEventGridCardV6 from './ScheduleEventGridCardV6';
import ScheduleEventCardV6 from './ScheduleEventCardV6';
import CrewLeaderEventCardV6 from '../views/cl/CrewLeaderEventCardV6';
import { hasPermissionsFor } from 'helpers/_helpers';
import { useRemoveSheet } from '../hooks/useRemoveSheet';
import { useDispatch } from 'react-redux';
import modalsPageActions from 'store/modals/modalsPageActions';

import './styles.scss';

/**
 * Shared visual representation of a schedule event.
 *
 * This component is reused by:
 * - DailyView (timeGridDay)
 * - WeeklyView (timeGridWeek)
 * - GridView (custom layout)
 * - CrewLeaderView
 */
const EventCard = ({
  event,
  eventType,
  locationColor,
  dateFormat,
  timeFormat,
  noClSheets,
  hasEditAccess,
}) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const selectedDate = useSelector((state) => state.schedule.selectedDate);

  const { sheet } = event?.extendedProps || {};
  const hasReadAccess = hasPermissionsFor('scheduleRead');

  const handleClick = (e) => {
    e.preventDefault();
    if (!sheet || (!hasEditAccess && !hasReadAccess)) return;
    const baseDate = selectedDate ? new Date(selectedDate) : new Date();
    const dateStr = format(baseDate, 'yyyy-MM-dd');
    navigate(`/schedule/${dateStr}?sheetId=${sheet._id}`);
  };

  const { removeSheet } = useRemoveSheet({ sheetId: event?.extendedProps?.sheet?._id });
  const handleClickDelete = (e) => {
    e.stopPropagation();
    if (!hasEditAccess) return;
    dispatch(
      modalsPageActions.openModal({
        name: 'remove',
        data: 'Are you sure you want to remove this project from the schedule?',
        onSubmit: (e) => removeSheet().then(() => dispatch(modalsPageActions.closeAllModals())),
      })
    );
  };
  
  const EventComponent = useMemo(() => {
    switch(eventType) {
      case 'grid': return ScheduleEventGridCardV6;
      case 'cl': return CrewLeaderEventCardV6;
      default: return ScheduleEventCardV6;
    }
  }, [eventType])

  if (!event) return null;

  return (
    <EventComponent
      event={event}
      locationColor={locationColor}
      dateFormat={dateFormat}
      timeFormat={timeFormat}
      handleClick={handleClick}
      handleDelete={handleClickDelete}
      noClSheets={noClSheets}
      hasEditAccess={hasEditAccess}
    />
  );
};

EventCard.propTypes = {
  event: PropTypes.oneOfType([
    PropTypes.object, // FullCalendar EventApi
  ]),
  eventType: PropTypes.string,
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  locationColor: PropTypes.string,
  canDragResources: PropTypes.bool,
  noClSheets: PropTypes.arrayOf(PropTypes.string),
  hasEditAccess: PropTypes.bool,
};

export default EventCard;

